import { useEffect, useRef, useState } from 'react';
import { Button } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { useWindowDimensions } from 'src/hooks';
import { capitaliseFirstLetter } from 'src/utils';
import {
  ButtonWrapper,
  LegendItemsContainer,
  LegendsDot,
  LegendsDotAndLine,
  LegendsItem,
  LegendsText,
} from './Legends.styles';

export interface LegendItemProps {
  color: string;
  text: string;
  active: boolean;
}

interface Props {
  items: LegendItemProps[];
  variant: 'dot' | 'dot+line';
  onClick?: (item: string) => void;
  'data-testid'?: string;
  alwaysShowOverflow?: boolean;
}

export const Legends = ({
  items,
  onClick,
  variant,
  alwaysShowOverflow = false,
  ...props
}: Props) => {
  const [overflowHidden, setOverflowHidden] = useState(false);
  const [showMoreButtonOpen, setShowMoreButtonOpen] = useState(false);
  const { width } = useWindowDimensions();
  const ref = useRef<HTMLDivElement>(null);
  const legendElement = ref.current;
  useEffect(() => {
    if (!legendElement) {
      return;
    }
    setOverflowHidden(
      alwaysShowOverflow ? false : legendElement.scrollHeight > 70,
    );
  }, [legendElement, width, alwaysShowOverflow]);
  return (
    <div>
      <LegendItemsContainer
        ref={ref}
        variant={variant}
        isInteractive={Boolean(onClick)}
        overflowHidden={overflowHidden}
        showMoreButtonOpen={showMoreButtonOpen}
      >
        {items.length > 0 &&
          items.map((item, index) => (
            <LegendsItem
              disabled={!item.active}
              key={`${item.text}-${index}`}
              onClick={() => {
                onClick?.(item.text);
              }}
            >
              {variant === 'dot' ? (
                <LegendsDot
                  color={item.color}
                  key={`legend-item-${index}`}
                  data-testid={props['data-testid'] ?? 'legends-dot'}
                />
              ) : (
                <LegendsDotAndLine
                  color={item.color}
                  key={`legend-item-${index}`}
                  data-testid={props['data-testid'] ?? 'legends-dot-and-line'}
                />
              )}
              <LegendsText title={capitaliseFirstLetter(item.text)}>
                {capitaliseFirstLetter(item.text)}
              </LegendsText>
            </LegendsItem>
          ))}
      </LegendItemsContainer>
      {overflowHidden && (
        <ButtonWrapper>
          <Button
            variant="secondary"
            size="small"
            onClick={() => {
              setShowMoreButtonOpen(
                (showMoreButtonOpen) => !showMoreButtonOpen,
              );
            }}
            data-testid={'view-more-button'}
            width="120px"
          >
            {showMoreButtonOpen ? 'Show less' : 'Show more'}
            <StyledIconInButton
              size="small"
              position="right"
              className={`uil uil-angle-${showMoreButtonOpen ? 'up' : 'down'}`}
            />
          </Button>
        </ButtonWrapper>
      )}
    </div>
  );
};
